// Итоговая панель варианта — sticky к низу колонки. Всё выводимое берётся из VariantTotals,
// расчёты не дублируются здесь.
import { useRef, useState } from 'react';
import type { CostBreakdown, VariantTotals } from '../../types';
import { formatDuration, formatMoney } from '../../core';
import { useStore } from '../../store';
import { useT } from '../../i18n';
import styles from './VariantTotalsPanel.module.css';

export interface VariantTotalsPanelProps {
  totals: VariantTotals;
}

/** Ключ подписи в разбивке — тот же, что у типа блока в формах. */
const BREAKDOWN_KEYS: Record<keyof CostBreakdown, string> = {
  flight: 'kind.flight',
  transfer: 'kind.transfer',
  lodging: 'kind.lodging',
  taxi: 'kind.taxi',
  food: 'kind.food',
  baggage: 'kind.baggage',
  misc: 'kind.misc',
};

export function VariantTotalsPanel({ totals }: VariantTotalsPanelProps) {
  const state = useStore();
  const t = useT();
  const currencies = state.currencies;
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const travelers = state.travelers;
  const showWithBaggage = totals.totalWithBaggageMinor !== totals.totalMinor;
  const showPerTraveler = travelers > 1;
  const perTravelerMinor = showPerTraveler ? Math.floor(totals.totalMinor / travelers) : 0;
  const perTravelerApprox = showPerTraveler && totals.totalMinor % travelers !== 0;

  const maxLevel = totals.warnings.reduce<'none' | 'warn' | 'error'>((acc, w) => {
    if (w.level === 'error') return 'error';
    if (w.level === 'warn' && acc !== 'error') return 'warn';
    return acc;
  }, 'none');

  const breakdownEntries = (Object.entries(totals.breakdown) as [keyof CostBreakdown, number][]).filter(
    ([, v]) => v !== 0,
  );

  function scrollToFirstWarning() {
    const first = totals.warnings[0];
    if (!first) return;
    const scope = panelRef.current?.closest('[data-drop-variant]');
    if (!scope) return;
    let el: Element | null = null;
    if (first.segmentId) {
      el = scope.querySelector(`[data-segment-id="${first.segmentId}"]`);
    } else if (first.gapIndex !== undefined) {
      el = scope.querySelector(`[data-connection-gap="${first.gapIndex}"]`);
    }
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  return (
    <div ref={panelRef} className={styles.panel}>
      <div className={styles.totalRow}>
        <span className={`${styles.total} mono`}>{formatMoney(totals.totalMinor, totals.currency, currencies)}</span>
        {totals.converted && <span className={styles.badge}>{t('totals.converted')}</span>}
      </div>
      {showWithBaggage && (
        <div className={styles.line}>
          <span>{t('totals.withBaggage')}</span>
          <span className="mono">{formatMoney(totals.totalWithBaggageMinor, totals.currency, currencies)}</span>
        </div>
      )}
      {showPerTraveler && (
        <div className={styles.line}>
          <span>{t('totals.perTraveler')}</span>
          <span className="mono">
            {perTravelerApprox ? '≈ ' : ''}
            {formatMoney(perTravelerMinor, totals.currency, currencies)}
          </span>
        </div>
      )}

      <div className={styles.metaRow}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{t('totals.flightTime')}</span>
          <span className="mono">{formatDuration(totals.flightTimeMin, t)}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{t('totals.doorToDoor')}</span>
          <span className="mono">{formatDuration(totals.doorToDoorMin, t)}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{t('totals.nights')}</span>
          <span className="mono">{totals.nights}</span>
        </div>
      </div>

      {totals.warnings.length > 0 && (
        <button
          type="button"
          className={`${styles.warnBtn} ${maxLevel === 'error' ? styles.warnBtnError : styles.warnBtnWarn}`}
          onClick={scrollToFirstWarning}
        >
          {t('totals.warnings', { count: totals.warnings.length })}
        </button>
      )}

      {breakdownEntries.length > 0 && (
        <div className={styles.breakdown}>
          <button
            type="button"
            className={styles.breakdownToggle}
            aria-expanded={breakdownOpen}
            onClick={() => setBreakdownOpen((v) => !v)}
          >
            <span>{t('totals.breakdown')}</span>
            <span className={styles.chevron} aria-hidden="true">
              {breakdownOpen ? '▾' : '▸'}
            </span>
          </button>
          {breakdownOpen && (
            <ul className={styles.breakdownList}>
              {breakdownEntries.map(([kind, minor]) => (
                <li key={kind} className={styles.breakdownItem}>
                  <span>{t(BREAKDOWN_KEYS[kind])}</span>
                  <span className="mono">{formatMoney(minor, totals.currency, currencies)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
